import { useState, useEffect, useCallback, useMemo, useRef, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import bomAPI, { getBOMExplosion, type BOMExplosionItem } from '@/api/bom.api';
import { useToast } from '@/contexts/ToastContext';
import { mrpService } from '@/services/mrpService';
import { apiClient } from '@/api/client';
import { useWarehouseStore } from '@/stores/warehouseStore';

// ─── Types ────────────────────────────────────────────────────────────────────
export interface ReservationShortageItem {
  id?: string;
  productionOrderId?: string;
  itemId: string;
  itemName: string;
  itemCode?: string;
  itemType?: string;
  unit?: string;
  unitPrice?: number;
  requiredQty: number;
  reservedQty?: number;
  issuedQty?: number;
  stockAvailable?: number;
  status?: string;
}

interface BOMExplosionModalProps {
  isOpen: boolean;
  onClose: () => void;
  bom: {
    id: string;
    code: string;
    version: string;
    productName?: string;
    product_name?: string;
    productId?: string;
    product_id?: string;
    productCode?: string;
    product_code?: string;
    unit?: string;
    unit_name?: string;
  };
  initialQty?: number;
  warehouseId?: string;
  warehouseName?: string;
  reservationShortages?: ReservationShortageItem[];
}

interface BaseExplosionItem extends BOMExplosionItem {
  qtyPerUnit: number;
}

// ─── Currency Formatter (Matches Screenshot: ₹1,620.00) ───────────────────────
function formatCurrencyWithDecimals(value: number | string): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '₹0.00';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
}

// ─── Type Badge (Exact Screenshot Styles) ─────────────────────────────────────
// Make-or-buy info for a semi-finished component: an active BOM means it is made in-house
// (production suggestion); no BOM means it is bought from a supplier (purchase suggestion).
function SemiFinishedInfo({ bom, loading }: { bom: { code: string } | null | undefined; loading: boolean }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const made = Boolean(bom);
  return (
    <span ref={ref} className="relative inline-flex">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Production or purchase info"
        className={`w-5 h-5 rounded-full inline-flex items-center justify-center transition-colors cursor-pointer ${
          loading ? 'text-slate-300' : made ? 'text-violet-600 hover:bg-violet-50' : 'text-sky-600 hover:bg-sky-50'
        }`}
      >
        <i className="ri-information-line text-sm" />
      </button>
      {open && (
        <span className="absolute left-0 top-6 z-30 w-72 rounded-xl border border-slate-200 bg-white shadow-xl p-3.5 text-left">
          {loading ? (
            <span className="flex items-center gap-2 text-xs text-slate-500">
              <i className="ri-loader-4-line animate-spin" /> Checking BOM…
            </span>
          ) : made ? (
            <>
              <span className="flex items-center gap-1.5 text-xs font-bold text-violet-700">
                <i className="ri-settings-3-line" /> Made in-house
              </span>
              <span className="block text-[11px] text-slate-600 mt-1.5 leading-relaxed">
                Has an active BOM <strong className="font-mono text-slate-800">{bom!.code}</strong>. Use{' '}
                <strong>Generate Production Order</strong> to create a production suggestion in MRP, then convert it to a
                production order when you are ready.
              </span>
            </>
          ) : (
            <>
              <span className="flex items-center gap-1.5 text-xs font-bold text-sky-700">
                <i className="ri-shopping-cart-2-line" /> Bought from supplier
              </span>
              <span className="block text-[11px] text-slate-600 mt-1.5 leading-relaxed">
                No active BOM, so it cannot be produced. Use <strong>Generate Purchase Suggestions</strong> to buy it.
              </span>
              <span className="block text-[11px] text-slate-500 mt-1.5 leading-relaxed">
                Make it in-house instead? Create and activate a BOM for it in <strong>Manufacturing → BOM</strong>.
              </span>
            </>
          )}
        </span>
      )}
    </span>
  );
}

function TypeBadge({ type }: { type: string }) {
  const norm = (type || 'RAW_MATERIAL').toUpperCase();

  if (norm.includes('RAW')) {
    return (
      <span className="inline-block px-2.5 py-0.5 rounded text-xs font-medium bg-[#fef3c7] text-[#92400e]">
        Raw
      </span>
    );
  }
  if (norm.includes('CONSUMABLE')) {
    return (
      <span className="inline-block px-2.5 py-0.5 rounded text-xs font-medium bg-[#e0f2fe] text-[#0369a1]">
        Consumable
      </span>
    );
  }
  if (norm.includes('PACKAGING')) {
    return (
      <span className="inline-block px-2.5 py-0.5 rounded text-xs font-medium bg-[#f1f5f9] text-[#475569]">
        Packaging
      </span>
    );
  }
  if (norm.includes('SEMI')) {
    return (
      <span className="inline-block px-2.5 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
        Semi
      </span>
    );
  }
  if (norm.includes('FINISH')) {
    return (
      <span className="inline-block px-2.5 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800">
        Finished
      </span>
    );
  }
  return (
    <span className="inline-block px-2.5 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-600">
      {type}
    </span>
  );
}

// ─── BOMExplosionModal Component ──────────────────────────────────────────────
export default function BOMExplosionModal({
  isOpen,
  onClose,
  bom,
  initialQty,
  warehouseId,
  warehouseName,
  reservationShortages,
}: BOMExplosionModalProps) {
  const toast = useToast();
  const navigate = useNavigate();

  const isShortageMode = Array.isArray(reservationShortages) && reservationShortages.length > 0;

  const [inputQty, setInputQty] = useState<string>('1');
  const [productionQty, setProductionQty] = useState<number>(1);
  const [baseItems, setBaseItems] = useState<BaseExplosionItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fullBom, setFullBom] = useState<any>(null);
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(new Set());
  const initialSelectionDoneRef = useRef(false);

  // Fetch complete BOM details if product_id is not already present on bom prop
  useEffect(() => {
    if (isOpen && bom?.id && !(bom as any).productId && !(bom as any).product_id) {
      bomAPI.getById(bom.id)
        .then((res) => {
          if (res?.success && res.data) {
            setFullBom(res.data);
          }
        })
        .catch((err) => {
          console.error('Failed to load full BOM in explosion modal:', err);
        });
    }
  }, [isOpen, bom?.id]);

  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // ── Fetch explosion data from backend ──
  const fetchExplosion = useCallback(
    async (qty: number, silent = false) => {
      if (!bom?.id) return;
      if (!silent) setLoading(true);
      setError(null);
      try {
        const targetWh = warehouseId || useWarehouseStore.getState().selectedWarehouseId;
        const result = await getBOMExplosion(bom.id, qty, targetWh && targetWh !== 'ALL' ? targetWh : undefined);
        const effectiveQty = result.productionQty || qty || 1;
        const items = result.items || [];
        if (items.length > 0) {
          const normalized: BaseExplosionItem[] = items.map((it) => ({
            ...it,
            qtyPerUnit: effectiveQty > 0 ? it.requiredQty / effectiveQty : it.requiredQty,
          }));
          setBaseItems(normalized);
        } else {
          setBaseItems([]);
          setError('This BOM has no components.');
        }
      } catch (err: any) {
        setBaseItems([]);
        setError(err?.message || 'Could not load the BOM explosion.');
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [bom?.id, warehouseId],
  );

  // Initial load when modal opens
  useEffect(() => {
    if (isOpen) {
      const initial = initialQty && initialQty > 0 ? initialQty : 1;
      setInputQty(String(initial));
      setProductionQty(initial);
      fetchExplosion(initial, false);
    } else {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      setBaseItems([]);
      setError(null);
    }
  }, [isOpen, bom?.id, initialQty, fetchExplosion]);

  // ── Real-Time Dynamic Recalculation ──
  type CalcItem = BaseExplosionItem & {
    originalRequiredQty?: number;
    reservedQty?: number;
    issuedQty?: number;
    isPartial?: boolean;
    isOutOfStock?: boolean;
  };

  const calculatedItems = useMemo((): CalcItem[] => {
    if (isShortageMode && reservationShortages) {
      // ── SHORTAGE ONLY MODE (From Production Order Material Reservation) ──
      // Show only materials the order still cannot get: what is neither issued nor reserved yet,
      // less the free stock that can still be reserved (e.g. Req 12, Issued 2, Stock 0 -> Shortage 10).
      const items: CalcItem[] = [];

      for (const resv of reservationShortages) {
        const reqTotal = Number(resv.requiredQty || 0);
        const reserved = Number(resv.reservedQty || 0);
        const issued = Number(resv.issuedQty || 0);
        const pendingQty = Math.max(0, reqTotal - reserved - issued);

        // Fully issued / reserved materials need nothing more
        if (pendingQty <= 0) continue;

        const baseMatch = baseItems.find(
          (b) => b.itemId === resv.itemId || (resv.itemCode && b.itemCode === resv.itemCode),
        );
        const unitPrice = Number(resv.unitPrice ?? baseMatch?.unitPrice ?? 0);
        const itemType = resv.itemType || baseMatch?.itemType || 'RAW_MATERIAL';
        const itemCode = resv.itemCode || baseMatch?.itemCode || 'ITM';
        const unit = resv.unit || baseMatch?.unit || 'Pcs';
        // Reservation stock is already free stock (physical − all reservations) in the order's warehouse.
        // Only the BOM explosion fallback is physical stock, so deduct this order's reservation there.
        const stockAvailable =
          resv.stockAvailable !== undefined && resv.stockAvailable !== null
            ? Math.max(0, Number(resv.stockAvailable) || 0)
            : Math.max(0, Number(baseMatch?.stockAvailable ?? 0) - reserved);
        const shortageQty = Number(Math.max(0, pendingQty - stockAvailable).toFixed(3));
        // Free stock covers the rest — it only has to be reserved, nothing needs buying
        if (shortageQty <= 0) continue;
        const lineCost = Math.round(shortageQty * unitPrice * 100) / 100;

        items.push({
          itemId: resv.itemId,
          itemName: resv.itemName,
          itemCode,
          itemType,
          unit,
          unitPrice,
          requiredQty: shortageQty,
          originalRequiredQty: reqTotal,
          reservedQty: reserved,
          issuedQty: issued,
          stockAvailable,
          diff: -shortageQty,
          isShort: true,
          lineCost,
          level: 1,
          qtyPerUnit: shortageQty,
          isPartial: reserved + issued > 0,
          isOutOfStock: reserved + issued === 0 && stockAvailable <= 0,
        });
      }

      return items;
    }

    // ── STANDARD BOM EXPLOSION MODE ──
    const qty = Math.max(1, productionQty || 1);
    const standardItems: CalcItem[] = baseItems.map((item) => {
      const reqQty = Number((item.qtyPerUnit * qty).toFixed(3));
      const lineCost = Math.round(reqQty * (item.unitPrice || 0) * 100) / 100;
      const diff = Number(((item.stockAvailable || 0) - reqQty).toFixed(3));
      const isShort = diff < 0;
      return {
        ...item,
        requiredQty: reqQty,
        lineCost,
        diff,
        isShort,
      };
    });

    const resultList = isShortageMode && reservationShortages ? items : standardItems;

    const getItemTypeRank = (type?: string): number => {
      const norm = String(type || '').toUpperCase();
      if (norm.includes('SEMI')) return 1;
      if (norm.includes('RAW')) return 2;
      return 3;
    };

    return resultList.sort((a, b) => {
      const rankA = getItemTypeRank(a.itemType);
      const rankB = getItemTypeRank(b.itemType);
      if (rankA !== rankB) return rankA - rankB;
      if (a.isShort !== b.isShort) return a.isShort ? -1 : 1;
      return (a.itemName || '').localeCompare(b.itemName || '');
    });
  }, [isShortageMode, reservationShortages, baseItems, productionQty]);

  // ── Categorized Groups: Semi-Finished first, then Raw Material, then Other Products ──
  const categorizedGroups = useMemo(() => {
    const semiFinished: CalcItem[] = [];
    const rawMaterials: CalcItem[] = [];
    const otherProducts: CalcItem[] = [];

    for (const it of calculatedItems) {
      const norm = String(it.itemType || '').toUpperCase();
      if (norm.includes('SEMI')) {
        semiFinished.push(it);
      } else if (norm.includes('RAW')) {
        rawMaterials.push(it);
      } else {
        otherProducts.push(it);
      }
    }

    const groups: Array<{ key: string; title: string; badgeTone: string; dotTone: string; count: number; items: CalcItem[] }> = [];
    if (semiFinished.length > 0) {
      groups.push({
        key: 'SEMI_FINISHED',
        title: 'Semi-Finished Goods',
        badgeTone: 'bg-amber-100 text-amber-800 border-amber-300',
        dotTone: 'bg-amber-500',
        count: semiFinished.length,
        items: semiFinished,
      });
    }
    if (rawMaterials.length > 0) {
      groups.push({
        key: 'RAW_MATERIAL',
        title: 'Raw Materials',
        badgeTone: 'bg-blue-100 text-blue-800 border-blue-300',
        dotTone: 'bg-blue-500',
        count: rawMaterials.length,
        items: rawMaterials,
      });
    }
    if (otherProducts.length > 0) {
      groups.push({
        key: 'OTHER',
        title: 'Other Products & Components',
        badgeTone: 'bg-slate-100 text-slate-800 border-slate-300',
        dotTone: 'bg-slate-500',
        count: otherProducts.length,
        items: otherProducts,
      });
    }
    return groups;
  }, [calculatedItems]);

  // ── Auto-select all items once when modal opens or items populate ──
  useEffect(() => {
    if (!isOpen) {
      initialSelectionDoneRef.current = false;
      setSelectedItemIds(new Set());
    } else if (!initialSelectionDoneRef.current && calculatedItems.length > 0) {
      initialSelectionDoneRef.current = true;
      setSelectedItemIds(new Set(calculatedItems.map((it) => it.itemId)));
    }
  }, [isOpen, calculatedItems]);

  const toggleSelectItem = useCallback((itemId: string) => {
    setSelectedItemIds((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  }, []);

  const allItemIds = useMemo(() => calculatedItems.map((it) => it.itemId), [calculatedItems]);

  const toggleSelectAll = useCallback(() => {
    setSelectedItemIds((prev) => {
      if (prev.size === allItemIds.length) {
        return new Set();
      } else {
        return new Set(allItemIds);
      }
    });
  }, [allItemIds]);

  const isAllSelected = allItemIds.length > 0 && selectedItemIds.size === allItemIds.length;
  const isIndeterminate = selectedItemIds.size > 0 && selectedItemIds.size < allItemIds.length;

  const isSemiFinished = useCallback((item: CalcItem) => {
    const norm = String(item.itemType || '').toUpperCase();
    return norm.includes('SEMI');
  }, []);

  const isRawMaterial = useCallback((item: CalcItem) => {
    const norm = String(item.itemType || '').toUpperCase();
    return norm.includes('RAW');
  }, []);

  // Active BOM per semi-finished item — decides "made in-house" vs "bought" in the info popover
  const semiItemIds = useMemo(
    () => [...new Set(calculatedItems.filter(isSemiFinished).map((it) => it.itemId).filter(Boolean))].sort(),
    [calculatedItems, isSemiFinished]
  );
  const semiItemKey = semiItemIds.join(',');
  const [activeBomByItem, setActiveBomByItem] = useState<Map<string, { code: string }>>(new Map());
  const [bomLookupLoading, setBomLookupLoading] = useState(false);
  useEffect(() => {
    if (!isOpen || !semiItemKey) return;
    let cancelled = false;
    setBomLookupLoading(true);
    apiClient
      .get<any>('/api/v1/manufacturing/bom', { params: { itemIds: semiItemKey, limit: 200 } })
      .then(({ data }) => {
        if (cancelled) return;
        const map = new Map<string, { code: string }>();
        for (const b of data?.data || []) {
          if (String(b.status || '').toUpperCase() === 'ACTIVE' && b.product_id && !map.has(String(b.product_id))) {
            map.set(String(b.product_id), { code: b.code });
          }
        }
        setActiveBomByItem(map);
      })
      .catch(() => { if (!cancelled) setActiveBomByItem(new Map()); })
      .finally(() => { if (!cancelled) setBomLookupLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen, semiItemKey]);

  const selectedItems = useMemo(() => {
    return calculatedItems.filter((it) => selectedItemIds.has(it.itemId));
  }, [calculatedItems, selectedItemIds]);

  const hasSelected = selectedItems.length > 0;
  const allSelectedAreSemi = hasSelected && selectedItems.every(isSemiFinished);

  // ── Button Enablement Rules:
  // 1. Raw material only -> only Purchase Suggestion enabled
  // 2. Semi-finished only -> BOTH Purchase Suggestion and Production Suggestion enabled
  // 3. Mixture (Raw + Semi) -> only Purchase Suggestion enabled
  // 4. Nothing selected -> both disabled
  const canGenerateProductionOrder = hasSelected && allSelectedAreSemi;
  const canGeneratePurchaseSuggestions = hasSelected;

  // Summary statistics
  const summary = useMemo(() => {
    let totalCost = 0;
    let shortageCount = 0;
    let sufficientCount = 0;

    for (const item of calculatedItems) {
      totalCost += item.lineCost;
      if (item.isShort) shortageCount++;
      else sufficientCount++;
    }

    return {
      totalCost,
      shortageCount,
      sufficientCount,
      totalItems: calculatedItems.length,
    };
  }, [calculatedItems]);

  // Handle Qty input change with real-time recalculation + debounced server sync
  const handleQtyChange = (val: string) => {
    setInputQty(val);
    const parsed = parseFloat(val);
    if (!isNaN(parsed) && parsed > 0) {
      setProductionQty(parsed);
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(() => {
        fetchExplosion(parsed, true);
      }, 400);
    }
  };

  const handleQtyBlur = () => {
    const parsed = Math.max(1, parseFloat(inputQty) || 1);
    setInputQty(String(parsed));
    setProductionQty(parsed);
    fetchExplosion(parsed, true);
  };

  // ── Generate MRP Purchase Suggestions ──
  const handleCreatePurchaseRequest = async () => {
    if (selectedItems.length === 0) return;
    setGenerating(true);
    const targetItems = selectedItems;
    const prodName = bom.productName ?? bom.product_name ?? '';
    const whId = warehouseId || useWarehouseStore.getState().selectedWarehouseId;
    const activeWarehouseId = whId && whId !== 'ALL' ? whId : undefined;

    try {
      const res = await mrpService.createFromBOM({
        bomId: bom.id,
        bomCode: bom.code,
        warehouseId: activeWarehouseId,
        productName: prodName,
        productionQty,
        items: targetItems.map((item) => ({
          itemId: item.itemId,
          itemName: item.itemName,
          shortQty: item.isShort ? Math.abs(item.diff) : item.requiredQty,
          requiredQty: item.requiredQty,
          unitPrice: item.unitPrice,
          unit: item.unit,
        })),
      });

      toast.success(`Created MRP purchase suggestions for ${targetItems.length} item(s)`);
      onClose();
      navigate(`/manufacturing/mrp/purchase?runId=${res.mrpRunId}`);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to create purchase suggestions');
    } finally {
      setGenerating(false);
    }
  };

  // ── Generate Production Suggestions for selected semi-finished items ──
  // Creates MRP production suggestions (not orders): the user reviews them in MRP → Production
  // Suggestions and converts them into production orders as per their requirement.
  const handleGenerateProductionOrder = async () => {
    const semiItems = selectedItems.filter(isSemiFinished);
    if (semiItems.length === 0) return;
    const whId = warehouseId || useWarehouseStore.getState().selectedWarehouseId;
    const activeWarehouseId = whId && whId !== 'ALL' ? whId : undefined;
    if (!activeWarehouseId) {
      toast.error('Select a warehouse first — production suggestions are planned per warehouse');
      return;
    }

    setGenerating(true);
    try {
      const res = await mrpService.createProductionSuggestionsFromBOM({
        warehouseId: activeWarehouseId,
        sourceProductionOrderId: reservationShortages?.find((r) => r.productionOrderId)?.productionOrderId,
        items: semiItems.map((item) => ({
          itemId: item.itemId,
          itemName: item.itemName,
          shortQty: item.isShort ? Math.abs(item.diff) : item.requiredQty,
          requiredQty: item.requiredQty,
        })),
      });
      toast.success(`Created ${res.created.length} production suggestion(s): ${res.created.map((c) => `${c.itemName} × ${c.qty}`).join(', ')}`);
      if (res.skipped?.length) toast.warning(`Skipped — ${res.skipped.join('; ')}`);
      onClose();
      navigate(`/manufacturing/mrp/purchase?runId=${res.mrpRunId}&tab=production`);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to create production suggestions');
    } finally {
      setGenerating(false);
    }
  };

  if (!isOpen) return null;

  const productName = bom.productName ?? bom.product_name ?? '';
  const bomCode = bom.code || '';
  const bomVersion = bom.version || '0.1';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-xs p-4 animate-in fade-in duration-150">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[92vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-150">
        {/* ── Header ── */}
        <div className="flex items-start justify-between px-7 pt-6 pb-2 bg-white">
          <div>
            <div className="flex items-center gap-2.5 flex-wrap">
              <h2 className="text-xl font-bold text-slate-800 tracking-tight">
                BOM Explosion — {productName}
              </h2>
              {isShortageMode && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-800 border border-amber-200">
                  <i className="ri-error-warning-fill text-amber-500 text-xs" />
                  Material Shortage ({summary.shortageCount})
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 mt-1">
              <p className="text-xs text-slate-400 font-medium">
                {bomCode} · v{bomVersion}
              </p>
              {(warehouseName || warehouseId) && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium bg-indigo-50 text-indigo-700 border border-indigo-200">
                  <i className="ri-building-line text-xs" />
                  {warehouseName || 'Active Warehouse'}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-slate-600 cursor-pointer transition-colors"
            aria-label="Close"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        {/* ── Production Quantity Control Bar ── */}
        {isShortageMode ? (
          <div className="px-7 pt-2 pb-4 flex items-center justify-between gap-3 bg-white border-b border-slate-100 flex-wrap">
            <div className="flex items-center gap-3">
              <span className="text-xs font-semibold text-slate-600">
                Order Planned Quantity:
              </span>
              <span className="px-2.5 py-0.5 bg-slate-100 rounded-lg text-xs font-bold text-slate-800 font-mono border border-slate-200">
                {initialQty || productionQty} units
              </span>
              <span className="text-[11px] text-amber-800 bg-amber-50 px-2.5 py-1 rounded-md border border-amber-200 font-medium flex items-center gap-1.5">
                <i className="ri-information-line text-xs text-amber-600" />
                Showing only materials still short for this order: required − issued − reserved − free stock.
              </span>
            </div>
          </div>
        ) : (
          <div className="px-7 pt-2 pb-5 flex items-center gap-3 bg-white">
            <span className="text-sm font-semibold text-slate-800 whitespace-nowrap">
              Production Quantity:
            </span>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={1}
                step={1}
                value={inputQty}
                onChange={(e) => handleQtyChange(e.target.value)}
                onBlur={handleQtyBlur}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleQtyBlur();
                }}
                className="w-24 h-9 px-3 text-center text-sm font-bold text-slate-800 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-2xs [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
              />
              <span className="text-sm text-slate-400 font-normal ml-1">units</span>
            </div>
          </div>
        )}

        {/* ── Materials Table ── */}
        <div className="flex-1 overflow-auto bg-white">
          {loading && baseItems.length === 0 && !isShortageMode && (
            <div className="flex flex-col items-center justify-center h-48 gap-2 text-slate-400">
              <i className="ri-loader-4-line animate-spin text-indigo-600 text-2xl" />
              <p className="text-sm font-medium">Loading BOM explosion…</p>
            </div>
          )}

          {error && baseItems.length === 0 && !isShortageMode && (
            <div className="flex flex-col items-center justify-center h-48 gap-2 text-sm">
              <i className="ri-error-warning-line text-rose-400 text-3xl" />
              <p className="text-rose-500 font-medium">{error}</p>
              <button
                onClick={() => fetchExplosion(productionQty, false)}
                className="mt-1 text-xs text-indigo-600 underline cursor-pointer"
              >
                Retry
              </button>
            </div>
          )}

          {isShortageMode && calculatedItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
              <div className="w-14 h-14 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mb-3.5 border border-emerald-200 shadow-xs">
                <i className="ri-checkbox-circle-fill text-3xl" />
              </div>
              <h3 className="text-base font-bold text-slate-800">No Material Shortage</h3>
              <p className="text-xs text-slate-500 max-w-md mt-1 leading-relaxed">
                Every component is already issued or reserved, or has enough free stock in the warehouse to be reserved. Nothing needs to be purchased for this order.
              </p>
            </div>
          ) : (!loading || baseItems.length > 0 || isShortageMode) && (
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-slate-50/70 border-y border-slate-100">
                  <th className="w-10 pl-6 pr-2 py-3 text-center">
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = isIndeterminate;
                      }}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                      title={isAllSelected ? 'Deselect all items' : 'Select all items'}
                    />
                  </th>
                  <th className="text-left pl-2 pr-4 py-3 text-xs font-semibold text-slate-500 tracking-wide">
                    Item
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 tracking-wide">
                    Code
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 tracking-wide">
                    Type
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 tracking-wide">
                    {isShortageMode ? 'Shortage Req. Qty' : 'Req. Qty'}
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 tracking-wide">
                    Unit
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 tracking-wide">
                    Stock
                  </th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 tracking-wide">
                    Short/Surplus
                  </th>
                  <th className="text-right pl-4 pr-7 py-3 text-xs font-semibold text-slate-500 tracking-wide">
                    Cost
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100/90">
                {categorizedGroups.map((group) => (
                  <Fragment key={group.key}>
                    <tr className="bg-slate-50/90 border-y border-slate-200">
                      <td colSpan={9} className="pl-6 pr-7 py-2.5 bg-gradient-to-r from-slate-50 to-slate-100/50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className={`w-2.5 h-2.5 rounded-full ${group.dotTone}`} />
                            <span className="text-xs font-bold text-slate-800 tracking-wide">
                              {group.title}
                            </span>
                            <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold border ${group.badgeTone}`}>
                              {group.count} {group.count === 1 ? 'item' : 'items'}
                            </span>
                          </div>
                          <span className="text-[11px] text-slate-500 font-medium">
                            Category Total: {group.items.reduce((acc, it) => acc + Number(it.requiredQty || 0), 0).toFixed(2)} units
                          </span>
                        </div>
                      </td>
                    </tr>
                    {group.items.map((item, idx) => {
                      const isChecked = selectedItemIds.has(item.itemId);
                      return (
                        <tr
                          key={item.itemId || `${group.key}_${idx}`}
                          className={`hover:bg-slate-50/50 transition-colors ${
                            isChecked ? 'bg-indigo-50/20' : ''
                          }`}
                        >
                          {/* Checkbox */}
                          <td className="w-10 pl-6 pr-2 py-4 text-center">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => toggleSelectItem(item.itemId)}
                              className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                            />
                          </td>

                          {/* Item Name */}
                          <td className="pl-2 pr-4 py-4 text-left">
                            <div className="font-medium text-slate-800 text-sm flex items-center gap-1">
                              <span>{item.itemName}</span>
                              {isSemiFinished(item) && (
                                <SemiFinishedInfo bom={activeBomByItem.get(item.itemId)} loading={bomLookupLoading} />
                              )}
                            </div>
                            {isShortageMode && (
                              <div className="mt-0.5">
                                {item.isPartial ? (
                                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200">
                                    <i className="ri-error-warning-line text-[10px]" />
                                    Partially covered ({item.issuedQty || 0} issued{Number(item.reservedQty) > 0 ? `, ${item.reservedQty} reserved` : ''} of {item.originalRequiredQty} {item.unit})
                                  </span>
                                ) : item.isOutOfStock ? (
                                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-rose-700 bg-rose-50 px-1.5 py-0.5 rounded border border-rose-200">
                                    <i className="ri-close-circle-line text-[10px]" />
                                    Completely Out of Stock (0 of {item.originalRequiredQty} {item.unit} reserved)
                                  </span>
                                ) : null}
                              </div>
                            )}
                          </td>

                          {/* Code */}
                          <td className="px-4 py-4 text-left text-xs text-slate-400 font-normal">
                            {item.itemCode}
                          </td>

                          {/* Type Badge */}
                          <td className="px-4 py-4 text-left">
                            <TypeBadge type={item.itemType} />
                          </td>

                          {/* Req. Qty */}
                          <td className="px-4 py-4 text-right">
                            <div className="text-sm font-bold text-slate-800 font-mono">
                              {Number(item.requiredQty).toFixed(3)}
                            </div>
                            {isShortageMode && (
                              <div className="text-[10px] text-rose-500 font-medium">
                                shortage needed
                              </div>
                            )}
                          </td>

                          {/* Unit */}
                          <td className="px-4 py-4 text-left text-sm text-slate-600 font-normal">
                            {item.unit || 'Kg'}
                          </td>

                          {/* Stock */}
                          <td className="px-4 py-4 text-right text-sm text-slate-700 font-normal font-mono">
                            {Number(item.stockAvailable).toFixed(
                              item.stockAvailable % 1 === 0 ? 0 : 3,
                            )}
                          </td>

                          {/* Short/Surplus Badge */}
                          <td className="px-4 py-4 text-center">
                            {!item.isShort ? (
                              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-600">
                                <span>✓</span> +{Number(item.diff).toFixed(3)} surplus
                              </span>
                            ) : item.isPartial ? (
                              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                                <span>⚠</span> -{Math.abs(Number(item.diff)).toFixed(3)} shortage
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-rose-50 text-rose-600">
                                <span>✕</span> -{Math.abs(Number(item.diff)).toFixed(3)} shortage
                              </span>
                            )}
                          </td>

                          {/* Cost */}
                          <td className="pl-4 pr-7 py-4 text-right text-sm font-bold text-slate-800 font-mono">
                            {formatCurrencyWithDecimals(item.lineCost)}
                          </td>
                        </tr>
                      );
                    })}
                  </Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* ── Footer / Summary Bar ── */}
        <div className="px-7 py-5 flex items-center justify-between gap-4 bg-white rounded-b-2xl border-t border-slate-100 flex-wrap sm:flex-nowrap">
          {/* Summary Stats (Left side) */}
          <div className="flex items-center gap-6 text-sm flex-wrap">
            <div className="text-slate-700 font-medium whitespace-nowrap">
              {isShortageMode ? 'Total Shortage Replenishment Cost: ' : 'Total Raw Material Cost: '}
              <span className="font-bold text-slate-900 ml-1">
                {formatCurrencyWithDecimals(summary.totalCost)}
              </span>
            </div>
            <div className="font-semibold text-rose-600 whitespace-nowrap">
              Items with Shortage: {summary.shortageCount}
            </div>
            {hasSelected && (
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 whitespace-nowrap">
                {selectedItems.length} of {calculatedItems.length} selected
              </span>
            )}
            {!isShortageMode && (
              <div className="font-semibold text-emerald-600 whitespace-nowrap">
                Items Sufficient: {summary.sufficientCount}
              </div>
            )}
          </div>

          {/* Action Buttons (Right side) */}
          <div className="flex items-center gap-2.5 shrink-0 ml-auto flex-wrap sm:flex-nowrap">
            <button
              type="button"
              onClick={handleGenerateProductionOrder}
              disabled={generating || !canGenerateProductionOrder}
              className={`h-10 px-4 rounded-xl text-xs font-bold shadow-xs flex items-center gap-1.5 transition-all whitespace-nowrap ${
                canGenerateProductionOrder && !generating
                  ? 'bg-violet-600 hover:bg-violet-700 text-white cursor-pointer active:scale-[0.98]'
                  : 'bg-slate-200 text-slate-400 cursor-not-allowed opacity-60'
              }`}
              title={
                !hasSelected
                  ? 'Select at least one semi-finished item to generate a production order'
                  : !allSelectedAreSemi
                  ? 'Production orders can only be generated for Semi-Finished goods. Please select only Semi-Finished items.'
                  : 'Create production suggestions in MRP for the selected semi-finished item(s) — convert them to production orders from MRP'
              }
            >
              {generating ? <i className="ri-loader-4-line animate-spin text-sm" /> : <i className="ri-settings-3-line text-sm" />}
              <span>Generate Production Order</span>
            </button>
            <button
              type="button"
              onClick={handleCreatePurchaseRequest}
              disabled={generating || !canGeneratePurchaseSuggestions}
              className={`h-10 px-4 rounded-xl text-xs font-bold shadow-xs flex items-center gap-1.5 transition-all whitespace-nowrap ${
                canGeneratePurchaseSuggestions && !generating
                  ? 'bg-[#4f46e5] hover:bg-[#4338ca] text-white cursor-pointer active:scale-[0.98]'
                  : 'bg-slate-200 text-slate-400 cursor-not-allowed opacity-60'
              }`}
              title={
                !hasSelected
                  ? 'Select at least one item to generate purchase suggestions'
                  : 'Generate purchase suggestions for selected item(s)'
              }
            >
              {generating ? (
                <i className="ri-loader-4-line animate-spin text-sm" />
              ) : (
                <i className="ri-shopping-cart-2-line text-sm" />
              )}
              <span>Generate Purchase Suggestions</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

